// §5a — WHY Google impression share is being lost.
//
// Lost-to-BUDGET and lost-to-RANK are two different conversations: spend more,
// versus fix bids and quality. This is the best panel available on either page
// and the data was already shaped for it.
//
// ⚠️⚠️ A NULL SHARE RENDERS "—", NEVER 0%. Google writes 0.0 rather than NULL
// where a share cannot apply, so a confident zero would tell a PMax-heavy
// client they captured none of an auction they were never in. The pure core
// hands us nulls; this file's only job is not to coerce them.

import { UNASSIGNED_AM_LABEL } from '../../lib/advertisingHome/bookFilter';
import { AccountLinkIcons } from '../../components/ui/AccountLinkIcons';
import type { AccountLinkKey } from '../../lib/accountLinks';
import { PanelTitle } from '../reporting/PanelTitle';
import {
  buildShareLossWorklist,
  countShareLossReasons,
  type CompetitivenessAccountRow,
  type ShareLossReason,
} from '../../lib/advertisingHome/impressionShare';
import { channelReportingHref } from './deepLinks';
import { Link } from 'react-router-dom';

const REASON_LABEL: Record<ShareLossReason, string> = {
  budget: 'Budget-limited',
  rank: 'Rank-limited',
  healthy: 'Competitive',
  not_measured: 'Not measured',
};

const REASON_CLASS: Record<ShareLossReason, string> = {
  budget: 'bg-amber-100 text-amber-800',
  rank: 'bg-destructive/10 text-destructive',
  healthy: 'bg-muted text-muted-foreground',
  not_measured: 'bg-muted text-muted-foreground',
};

/** ⚠️ null → "—". The one rule this component exists to hold. */
function pct(v: number | null): string {
  return v === null ? '—' : `${v.toFixed(1)}%`;
}

export function ShareLossPanel({
  rows,
  loading,
  nameOf,
  amNameOf,
  linkKeys,
}: {
  linkKeys?: AccountLinkKey[];
  rows: readonly CompetitivenessAccountRow[];
  loading: boolean;
  nameOf: (id: string) => string;
  amNameOf: (accountId: string | null) => string | null;
}): React.JSX.Element {
  const items = buildShareLossWorklist(rows);
  const counts = countShareLossReasons(items);

  return (
    <section className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <PanelTitle>Why the auction is being lost</PanelTitle>
        {items.length > 0 && (
          <span className="text-xs text-muted-foreground">
            {counts.budget} budget-limited · {counts.rank} rank-limited
            {counts.not_measured > 0 ? ` · ${counts.not_measured} not measured` : ''}
          </span>
        )}
      </div>

      {/* ⚠️ THE TWO LIMITS, STATED RATHER THAN DISCOVERED. Campaign grain is all
          Google gives (there is no ad-group cross-device spec), and a Location
          with no Search auction is genuinely unmeasurable rather than at zero. */}
      <p className="text-xs text-muted-foreground">
        Search campaigns only — Performance Max, Display and Video carry no impression share,
        so a Location running those alone reads &ldquo;not measured&rdquo; rather than 0%.
      </p>

      {loading ? (
        <p className="text-sm text-muted-foreground">Reading the auction…</p>
      ) : items.length === 0 ? (
        <p className="rounded-lg border border-border bg-card p-4 text-sm text-muted-foreground">
          No measurable Search auction in this range.
        </p>
      ) : (
        <div className="max-h-[32rem] overflow-y-auto rounded-lg border border-border scroll-subtle">
          <table className="w-full border-collapse text-sm">
            <thead className="sticky top-0 bg-card shadow-[inset_0_-1px_0_var(--border)]">
              <tr className="text-left text-xs text-muted-foreground">
                <th className="px-3 py-2 font-medium">Account</th>
                <th className="px-3 py-2 font-medium">Verdict</th>
                <th className="px-3 py-2 text-right font-medium">Share won</th>
                <th className="px-3 py-2 text-right font-medium">Lost to budget</th>
                <th className="px-3 py-2 text-right font-medium">Lost to rank</th>
                <th className="px-3 py-2 font-medium">Account manager</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.accountId} className="border-t border-border hover:bg-accent/40">
                  <td className="max-w-[16rem] px-3 py-2">
                    <span className="flex min-w-0 items-center gap-1">
                      <span className="truncate" title={nameOf(r.accountId)}>
                        {nameOf(r.accountId)}
                      </span>
                      {linkKeys && (
                        <AccountLinkIcons accountId={r.accountId} className="shrink-0" only={linkKeys} />
                      )}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <span
                      className={`inline-block rounded px-1.5 py-0.5 text-xs font-medium ${REASON_CLASS[r.reason]}`}
                    >
                      {REASON_LABEL[r.reason]}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{pct(r.impressionShare)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{pct(r.budgetLostShare)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{pct(r.rankLostShare)}</td>
                  <td className="max-w-[10rem] truncate px-3 py-2 text-muted-foreground">
                    {amNameOf(r.accountId) ?? UNASSIGNED_AM_LABEL}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Link
                      to={channelReportingHref('google', r.accountId)}
                      className="text-xs font-medium text-kyck-700 hover:underline"
                    >
                      View
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
